import { CurrencyPipe } from '@angular/common';
import { CompareMetric, MetricFormat, TierDistribution } from '../../models/loyalty-dashboard.model';

const currencyPipe = new CurrencyPipe('en-US');

/** The "nice" rounding step a domain's min/max are rounded outward to, per metric format. */
export function niceStepFor(format: MetricFormat): number {
  switch (format) {
    case 'currency':
    case 'percent':
    case 'signedPercent':
      return 1;
    case 'decimal':
      return 0.1;
  }
}

function floorToStep(value: number, step: number): number {
  return Math.round(Math.floor(value / step) * step * 1e10) / 1e10;
}

function ceilToStep(value: number, step: number): number {
  return Math.round(Math.ceil(value / step) * step * 1e10) / 1e10;
}

/** Every value a row's scale needs to span: `you`, plus each present tier's p25/median/p75. */
export function gatherDomainValues(metric: CompareMetric): number[] {
  const values: number[] = [];
  if (metric.you != null) {
    values.push(metric.you);
  }
  for (const tier of [metric.withLoyalty, metric.withoutLoyalty]) {
    if (tier) {
      values.push(tier.p25, tier.median, tier.p75);
    }
  }
  return values;
}

/** Min/max span for a row's track, padded 15% each side and rounded outward to a "nice" step. */
export function computeDomain(values: number[], format: MetricFormat): { min: number; max: number } {
  const finite = values.filter((v) => Number.isFinite(v));
  const step = niceStepFor(format);
  if (finite.length === 0) {
    return { min: 0, max: step };
  }

  const rawMin = Math.min(...finite);
  const rawMax = Math.max(...finite);
  const span = rawMax - rawMin;
  const pad = span > 0 ? span * 0.15 : Math.max(Math.abs(rawMax), step) * 0.15;

  const min = floorToStep(rawMin - pad, step);
  let max = ceilToStep(rawMax + pad, step);
  if (max === min) {
    max = min + step;
  }
  return { min, max };
}

/** Position of `value` along [min, max], as a percent clamped to 0–100. */
export function valueToPercent(value: number, min: number, max: number): number {
  if (max === min) {
    return 50;
  }
  const pct = ((value - min) / (max - min)) * 100;
  return Math.min(100, Math.max(0, pct));
}

/** Left/width percentages for a tier's "middle half" band (p25 to p75), plus its median tick position. */
export function tierBandPercents(
  tier: TierDistribution,
  min: number,
  max: number
): { left: number; width: number; median: number } {
  const left = valueToPercent(tier.p25, min, max);
  const right = valueToPercent(tier.p75, min, max);
  return { left, width: Math.max(0, right - left), median: valueToPercent(tier.median, min, max) };
}

/**
 * Where the zero line falls (as a track percent), or null when the format isn't signed or the row's
 * domain doesn't actually straddle zero. Generic to the `signedPercent` format, not any one metric.
 */
export function zeroLinePercent(min: number, max: number, format: MetricFormat): number | null {
  if (format !== 'signedPercent' || !(min < 0 && max > 0)) {
    return null;
  }
  return valueToPercent(0, min, max);
}

export function formatMetricValue(value: number, format: MetricFormat, decimals?: number): string {
  switch (format) {
    case 'currency':
      return currencyPipe.transform(value, 'USD', 'symbol', `1.${decimals ?? 2}-${decimals ?? 2}`) ?? '';
    case 'percent':
      return `${value.toFixed(decimals ?? 0)}%`;
    case 'signedPercent': {
      const formatted = value.toFixed(decimals ?? 0);
      return value > 0 ? `+${formatted}%` : `${formatted}%`;
    }
    case 'decimal':
      return value.toFixed(decimals ?? 1);
  }
}
